// Neutralize browser extensions (e.g. ClickUp) that monkey-patch core DOM APIs
// like EventTarget.addEventListener. Such patches can throw inside React's
// useSyncExternalStore / router at render time ("n is not a function") and white-screen the app.
//
// Strategy: borrow pristine native methods from a fresh, same-origin iframe
// (which the page's extensions haven't patched) and restore them on the main realm.
// Best-effort and fully guarded — if anything is non-configurable, we skip it.

let done = false;

export function hardenGlobals(): void {
  if (done) return;
  done = true;
  try {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.tabIndex = -1;
    iframe.style.cssText =
      "position:absolute;left:-9999px;top:-9999px;width:0;height:0;border:0;visibility:hidden";
    (document.body || document.documentElement).appendChild(iframe);
    const clean = iframe.contentWindow as (Window & typeof globalThis) | null;
    if (clean) {
      // 1) Restore EventTarget prototype methods (what extensions usually patch)
      try {
        const cleanProto = clean.EventTarget && clean.EventTarget.prototype;
        const mainProto = window.EventTarget && window.EventTarget.prototype;
        if (cleanProto && mainProto) {
          for (const k of ["addEventListener", "removeEventListener", "dispatchEvent"] as const) {
            try {
              if (typeof cleanProto[k] === "function") {
                (mainProto as any)[k] = cleanProto[k];
              }
            } catch {
              /* non-configurable — skip */
            }
          }
        }
      } catch {
        /* ignore */
      }
      // 2) Restore instance methods on window/history as a fallback
      const bind = (target: any, source: any, keys: string[]) => {
        for (const k of keys) {
          try {
            if (typeof source[k] === "function") target[k] = source[k].bind(target);
          } catch {
            /* skip */
          }
        }
      };
      bind(window, clean, ["addEventListener", "removeEventListener", "dispatchEvent"]);
      if (window.history && clean.history) {
        bind(window.history, clean.history, ["pushState", "replaceState"]);
      }
    }
    // Keep the iframe attached (hidden) so the borrowed native references stay valid.
  } catch {
    /* best-effort only */
  }
}
